import { clubSession, memberSession } from '../auth/instance';
import { isApiMode } from './baseUrl';
import { getImpersonationTenantId, registerResolvedTenant } from './impersonationToken';
import { beginScopeTransition } from './scopeTransition';

// ═══════════════════════════════════════════════════════════════════
// WHO IS THIS REQUEST FROM, AND WHERE (#750, M2b-2).
//
// ONE PLACE that answers three questions the transport used to answer by
// coincidence:
//
//   which credential does this request carry?
//   which organization does it address?
//   which session does a 401 end?
//
// ── WHY IT STOPPED BEING OBVIOUS ──────────────────────────────────
//
// `http.ts` said so in its own comment, and the comment was the design:
//
//     «A browser is one of these, not several: the portal and the member app
//      are different surfaces of the same bundle and are never signed in at
//      once.»
//
// That premise is what the unified-identity work removes. A browser already
// holds both through the dev-persona route, and after M2b-3 an account session
// serves both surfaces. The moment two credentials can be live, «whichever
// happens to exist, in a fixed order» stops being a rule and becomes a race
// between two sessions for the same header.
//
// ── THE SURFACE DECIDES, NOT THE INVENTORY ────────────────────────
//
// The app knows which surface it is rendering. That is the honest input: in
// MEMBER MODE a request belongs to the member and goes out with the member's
// credential and the MEMBER'S CLUB; on the club surface it belongs to the
// account. Asking «which tokens do I have» answers a different question, and
// answered it wrongly whenever the browser had both.
//
// THE BUG THIS REMOVES is not hypothetical. `getTenantId` returned
// `clubSession.tenantId()` — the ADMINISTRATIVE context — for every request
// including the member app's, and `getToken` preferred the club token. With
// both sessions live, a member request went out as the account user for the
// club an administrator had selected. It is invisible today only because
// `/member/*` takes its tenant from the session and refuses an `sps_` token
// outright; both of those change in M2b-3, and then it is a wrong actor rather
// than a refusal (#763 review).
// ═══════════════════════════════════════════════════════════════════

/** Everything a member screen needs, as the API projects it. */
export interface MemberBootstrap {
  me: Record<string, unknown> | null;
  members: Record<string, unknown>[];
  bookings: Record<string, unknown>[];
  activities: Record<string, unknown>[];
  plannedMatches: Record<string, unknown>[];
  singleMatches: Record<string, unknown>[];
  history: Record<string, unknown>[];
  club: Record<string, unknown> | null;
  bookingConfig: Record<string, unknown> | null;
  bookingZones: Record<string, unknown>[] | null;
  assurance: string | null;
}

/**
 * Is the member session driving this app?
 *
 * Both halves matter. Off the `http` backend there is no API to read from, and
 * without a SESSION `/member/bootstrap` answers 401 — so this is the one
 * condition under which the member loader replaces the Storage one.
 *
 * ── AND IT ASKS `isApiMode()`, NOT `Storage.backend` ──────────────
 *
 * The same question — «is there an API to read from» — from a LEAF module.
 * `storage.ts` imports the transport, so reading `Storage.backend` here would
 * make the transport import storage, and the damage is not a build error: it
 * moves WHEN `storage.ts` is evaluated, so the backend is decided before the
 * configured URL exists and every member read silently takes the memory path.
 *
 * `isApiMode()` is also a LIVE read where `Storage.backend` is fixed at module
 * evaluation, which is the more honest answer to a question about what is
 * reachable now.
 *
 * ── «SIGNED IN» IS NOT «HOLDS A TOKEN» (#620) ──────────────────────
 *
 * This asked `memberSession.token() !== null`, which was the same question right
 * up until the session also began travelling as a cookie. After a reload the
 * token is null BY DESIGN and the cookie authenticates — so every one of the
 * fifteen call sites silently decided this browser was not a member, and the app
 * took the CLUB path for reads and writes.
 *
 * The visible damage was not «a 401 somewhere». A browser holding a club cookie
 * too — every dev-persona browser does — booked through `POST /bookings`, which
 * honours the `paymentStatus` the CLIENT sends, so a priced court was written
 * `free` and the payment sheet never appeared. Money silently not owed is a worse
 * outcome than an error, and nothing failed anywhere to say so.
 *
 * So it asks the session what it IS, exactly as `clubSession` consumers do. The
 * token remains the right question for the TRANSPORT — whether to send a bearer
 * header — and that is the only place it is still asked.
 */
export function memberModeActive(): boolean {
  return isApiMode() && memberSession.state().status === 'signed-in';
}

/**
 * On the PLAYER surface, was this identity PROVEN rather than picked? (#791)
 *
 * ── THE PROPERTY IS «PROVEN», NOT «WHICH CREDENTIAL» ──────────────
 *
 * `useCurrentMember` used to ask `memberModeActive()`, which names the legacy
 * member family. That was the right answer while `spm_` was the only way to be
 * a player — and the player-first door (#710) makes it wrong by construction:
 * somebody signed in through `/auth/otp` holds an ACCOUNT session, so the
 * member check is false, and the workspace would fall back to its stored pick
 * and ask a verified person to CHOOSE A PLAYER TO BE. The pick would then be
 * honoured, because `setMemberId` only refuses in member mode.
 *
 * Nothing is broken while the player home is a leaf. It becomes a privilege
 * escalation the first time the home links into the workspace, which is why
 * #791 is a gate on that work rather than a follow-up to it.
 *
 * ── AND IT IS THE SURFACE THAT DECIDES ────────────────────────────
 *
 * Not «is any account session live»: an administrator in the club portal holds
 * one too, and their «who am I» pick (`demo_user_v1`) is the product — a club
 * logs in at club level and the person using it says which player they are.
 * That picker stays honest, and so does the one on the memory backend, where
 * there is no server to prove anything. Both are excluded by asking about the
 * MEMBER surface rather than about the inventory of tokens.
 *
 * A CREDENTIAL HAS TO BE PRESENT, not merely a selection: `customerContext`
 * treats an explicit surface choice as final even when its session is missing,
 * which is right for routing a request and would be wrong here — «proven by
 * nothing» is exactly what this refuses.
 */
export type PlayerIdentityMode = 'member-session' | 'account' | 'picked';

/**
 * ONE DECISION, so nothing can disagree with it (#791 review).
 *
 * The first version of this was a predicate beside a separate
 * `memberModeActive()` branch in `useCurrentMember`, and the two did not say
 * the same thing: the predicate claimed «the surface decides» while the
 * member-session check ignored the surface entirely. With BOTH families signed
 * in — which a dev-persona browser routinely is — and `'club'` explicitly
 * selected, the predicate answered «proven» and the portal's own picker went
 * away, while the hook took the legacy member's identity for a surface whose
 * credential is the account's.
 *
 * So the mode is resolved once, here, and the hook branches on it. Two things
 * cannot disagree when there is only one of them.
 *
 *   'member-session'  the legacy member credential names this person
 *   'account'         the account session does, on the player surface
 *   'picked'          nobody proved anything: the person says who they are
 *
 * LEGACY PRECEDENCE IS PRESERVED **WITHIN** THE MEMBER SURFACE, which mirrors
 * `memberAuthEither` on the server: two credential families, chosen by
 * presence, the member one winning. What changed is that the choice is now
 * made inside the surface rather than across it.
 */
export function playerIdentityMode(): PlayerIdentityMode {
  // Nothing can be proved without a server, and the memory backend's picker is
  // the honest answer there.
  if (!isApiMode()) return 'picked';
  // THE SURFACE FIRST. On the club surface the «logged in as» pick is the
  // product — a club logs in at club level and the person using it says which
  // player they are — and that is true even for a browser that also holds a
  // live member session.
  if (customerContext().surface !== 'member') return 'picked';
  if (memberSession.state().status === 'signed-in') return 'member-session';
  if (clubSession.state().status === 'signed-in') return 'account';
  // An explicit surface selection with no credential behind it. Final for
  // routing a request, and «proven by nothing» here.
  return 'picked';
}

/**
 * Is this the PLAYER SURFACE, with a person the server has named?
 *
 * ONE CONDITION, AND IT ANSWERS TWO QUESTIONS (#800). They turn out to be the
 * same question, which is why there is one function and the call sites name
 * their own use of it:
 *
 *   IDENTITY      is «who you are» the server's answer, or a local pick?
 *                 (#791 — the picker, and the refusal to switch)
 *   DATA SOURCE   does this surface read the MEMBER projections, or the club's
 *                 own `/kv` keys? (#800 — the loaders)
 *
 * Both are «does the server own this surface», and splitting them into two
 * predicates is exactly how the identity half came to be right while the
 * loaders stayed keyed on the credential family: an account-only player had an
 * immutable, correct id and no data, and a legacy member in the club portal
 * had the member's data under an administrator's surface.
 *
 * Named `playerIdentityIsProven` until #800, which is the identity half's view
 * of it; the condition is the surface's.
 */
export function playerSurfaceActive(): boolean {
  return playerIdentityMode() !== 'picked';
}

/**
 * Which surface a request belongs to.
 *
 * `staff` is NOT «nobody» — it is Starpoint's own people on `/sa`, and the
 * IMPERSONATION path, where an employee operating a customer's organization
 * holds no club session at all. Naming it was forced by getting it wrong: with
 * impersonation folded in as a `club` surface, the credential decision reached for a club
 * token that does not exist and the request went out unauthenticated.
 */
export type CustomerSurface = 'member' | 'club' | 'staff';

export interface CustomerContext {
  surface: CustomerSurface;
  /**
   * The organization this request ADDRESSES, or null.
   *
   * On the member surface it is the member's own club — never the one an
   * administrator happens to have selected. An id, because
   * `X-Starpoint-Tenant` carries an id and a slug would not resolve.
   */
  tenantId: string | null;
  /** Who the app believes it is acting as. Display and assertions, not authority. */
  actorId: string | null;
}

/**
 * The surface the app has EXPLICITLY selected, when something has selected one.
 *
 * ── WHY AN EXPLICIT INPUT EXISTS AT ALL (#764 review) ─────────────
 *
 * The default below infers the surface from which sessions are signed in, and
 * that inference is a COMPATIBILITY DEFAULT rather than the contract. It is
 * wrong for the product that is being built: under one account with several
 * workspaces, both a member session and a club session are live at once, and
 * «which is signed in» stops distinguishing anything — a person who has
 * navigated into an administrator's workspace would still be read as a member.
 *
 * So the shell (#751) and the workspace switcher (#752) SET this, and the
 * inference is what answers until they exist. Canonising «login inventory» as
 * the permanent selector would reintroduce the wrong-bearer problem one layer
 * up, at exactly the moment the design stops making it detectable.
 *
 * Mirrors `setImpersonationContext`, which is the same shape of answer for the
 * same kind of question.
 */
let selected: CustomerSurface | null = null;

/** Choose the surface. `null` restores the inferred default. */
export function selectCustomerSurface(surface: CustomerSurface | null): void {
  // THE ADDRESS MAY CHANGE WITH THE SURFACE — the portal's organization to
  // the player's club, or to none — and a write still in flight from the old
  // one must not follow (lib/api/scopeTransition.ts).
  const before = customerContext().tenantId;
  selected = surface;
  beginScopeTransition(before, customerContext().tenantId);
}

// An impersonation context starting, switching or ending changes the address
// too; its module cannot import this one (this one imports it), so it asks
// through a hook for the tenant as the transport resolves it.
registerResolvedTenant(() => customerContext().tenantId);

/** What has been selected, or null when nothing has. */
export function selectedCustomerSurface(): CustomerSurface | null {
  return selected;
}

/** A club a PLAYER has entered — `/me/clubs` gives all three fields (#800). */
export interface PlayerClub {
  /** The organization id, which is what `X-Starpoint-Tenant` carries. */
  id: string;
  slug: string;
  name: string;
}

/**
 * THE CLUB A PLAYER HAS ENTERED (#800).
 *
 * The account branch below used to answer with NO TENANT, and said why: *«not
 * «the first club» and not an administrator's selection: entering a club
 * context is a later, explicit action validated by the server»*. This is that
 * action, and the three properties the sentence promised are kept:
 *
 *   EXPLICIT      somebody tapped a club in their own list. Nothing infers it,
 *                 and no club is chosen on their behalf when they have one.
 *   THEIR OWN     the list comes from `/me/clubs`, which reads
 *                 `tenant_players` — never `/auth/scopes`, which answers a
 *                 different question about administering.
 *   VALIDATED     naming it grants nothing. `memberContextFromAccount` checks
 *                 the roster row for the organization the request names, so a
 *                 crafted id is refused with the id in hand.
 *
 * IN MEMORY, like the impersonation token and every bearer in this app. A
 * reload therefore returns a player to their club list rather than into a club
 * — which is the honest consequence of not persisting it, and the list is the
 * entrance anyway. Persisting a club selection would be a second source of
 * truth about where somebody is, and the one thing worse than re-entering a
 * club is being somewhere you did not choose to be after a refresh.
 */
let playerClub: PlayerClub | null = null;

/** Enter a club, or leave it (`null`). */
export function selectPlayerClub(club: PlayerClub | null): void {
  // The 📍 switch happens in place; the member bootstrap empties and reloads
  // the club slices, and until it has, a write begun in the previous club is
  // refused (lib/api/scopeTransition.ts).
  const before = customerContext().tenantId;
  playerClub = club;
  beginScopeTransition(before, customerContext().tenantId);
}

/** The club this player has entered, or null for «still in the list». */
export function selectedPlayerClub(): PlayerClub | null {
  return playerClub;
}

/**
 * The context as it is right now.
 *
 * Read per request rather than cached: a member signing out, a scope being
 * switched and an impersonation starting all change the answer, and a cached
 * one would send the previous surface's credential for a while afterwards.
 */
export function customerContext(): CustomerContext {
  // AN EXPLICIT SELECTION IS FINAL, including when the matching session is
  // missing: the answer is then that surface with no credential, which sends
  // nothing and is refused. Falling back to another surface because this one
  // has no token is the cross-family substitution this module exists to stop.
  if (selected === 'staff') return { surface: 'staff', tenantId: null, actorId: null };
  if (selected === 'member' || (selected === null && memberModeActive())) {
    const state = memberSession.state();
    if (state.status === 'signed-in') {
      return {
        surface: 'member',
        // THE MEMBER'S CLUB. Null when the session predates `club.id` — see
        // `memberTenantId` below; a null address is better than another club's.
        tenantId: state.club?.id ?? null,
        actorId: state.member?.id ?? null,
      };
    }
    // ── A PLAYER WITH NO CLUB, ON AN ACCOUNT SESSION (#710) ─────────
    //
    // The player-first door issues an `sps_` account session for an address
    // that may belong to no club at all, and a person in that state is a
    // first-class application state rather than an error. They are on the
    // MEMBER surface — they are a player, not an administrator — and the
    // credential they hold is the account's.
    //
    // THIS IS NOT THE FALLBACK THE RULE BELOW FORBIDS, and the difference is
    // the one the server already draws. `memberAuthEither` accepts two
    // credential families on this surface and chooses by PRESENCE, with the
    // legacy member credential winning; what is forbidden is reaching for
    // another family AFTER a refusal, which makes «which credential answered»
    // unknowable. This mirrors the server's choice rather than retrying.
    //
    // THE CLUB THEY ENTERED, AND ONLY THAT (#800). Still not «the first club»
    // and still not an administrator's selection: `selectedPlayerClub()` is
    // set by a player tapping a club in their OWN list and is null until they
    // do, so a player who has not entered one addresses nothing — which is
    // what `/me/clubs` and `/me/player` need, since neither is tenant-scoped.
    const account = clubSession.state();
    if (account.status === 'signed-in') {
      return {
        surface: 'member',
        tenantId: playerClub?.id ?? null,
        actorId: account.account?.id ?? null,
      };
    }
    // Neither credential. The surface is still the member one — an explicit
    // selection is final — and it sends nothing, which is refused.
    return { surface: 'member', tenantId: null, actorId: null };
  }
  // ── IMPERSONATION IS A STAFF SURFACE THAT ADDRESSES A CLUB (RBAC-3) ──
  //
  // Both halves matter and the first version had only the second. A Starpoint
  // employee operating a customer's organization holds NO club session, so the
  // credential is the Entra one — calling this a `club` surface made
  // the credential decision reach for a club token that does not exist, and the request
  // went out with no `Authorization` at all. The ADDRESS is the organization
  // being operated, which is the half that was already right.
  const impersonated = getImpersonationTenantId();
  if (impersonated) return { surface: 'staff', tenantId: impersonated, actorId: null };

  const club = clubSession.state();
  if (selected === 'club' || club.status === 'signed-in') {
    return {
      surface: 'club',
      tenantId: clubSession.tenantId(),
      actorId: club.account?.id ?? null,
    };
  }
  // NO CUSTOMER SESSION. `/sa` runs on the same transport and must keep
  // working, so this is the staff surface rather than «nothing».
  return { surface: 'staff', tenantId: null, actorId: null };
}

/**
 * The credential this request should carry, or null for «none of ours».
 *
 * NOT A FALLBACK CHAIN. Each surface has one credential, and a surface whose
 * credential is missing sends NOTHING rather than reaching for another's —
 * which would be the client-side version of the no-fallback rule the server
 * follows. A request with no credential is refused; a request with somebody
 * else's is a wrong actor.
 */
export function credentialFor(context: CustomerContext): string | null {
  if (context.surface === 'member') {
    // ── TWO FAMILIES ON THIS SURFACE, CHOSEN BY PRESENCE (#710) ────
    //
    // The legacy `spm_` member session wins when there is one — the same
    // precedence `memberAuthEither` applies on the server, and for the same
    // reason: the two credentials name two different PLAYERS, so taking the
    // stronger one would substitute the account's player for the member's.
    //
    // The account credential is reached only when there is NO member session,
    // which is the player-first state: signed in, belonging to no club. Not a
    // retry after a refusal — that is the shape the no-fallback rule forbids.
    const member = memberSession.token();
    if (member) return member;
    if (memberSession.state().status === 'signed-in') {
      // A COOKIE-RESTORED MEMBER HOLDS NO BEARER (#620), and must not borrow
      // the account's: the cookie answers, and reaching further would be the
      // cross-family substitution #764 found.
      return null;
    }
    return clubSession.token();
  }
  if (context.surface === 'club') return clubSession.token();
  return null;
}

/**
 * What a request may authenticate with.
 *
 * ── WHY «NO BEARER» IS TWO DIFFERENT ANSWERS (#839 re-review) ─────
 *
 * The decision used to answer `string | null`, and the transport read null as «send
 * no Authorization header» — while still sending `credentials: 'include'`,
 * because the cookie is what answers after a reload. For a customer surface
 * that is exactly right. On the STAFF surface it is a fail-open:
 *
 *   `staffAuth.getApiToken()` returns null on several ordinary failures — MSAL
 *   failing to initialise, a silent acquisition error that is not
 *   `interaction_required`, a failed interactive attempt — and two of those
 *   three do not move the state out of `signed-in`. So the panel still showed
 *   Entra account A while the request went out with no bearer, and the server,
 *   which deliberately consults the cookie only when no `Authorization` is
 *   present, authenticated the browser's CUSTOMER session B instead. A wrong
 *   actor on `/me` and on platform writes, and a wrong name in the audit row.
 *
 * So the answer is three-valued, and the third value is the point:
 *
 *   `bearer`   this credential, as an `Authorization` header.
 *   `ambient`  no bearer, and the cookie IS this surface's credential — a
 *              cookie-restored customer session, or the dev staff OTP session
 *              on a deployment with no Entra identity at all.
 *   `none`     FAIL CLOSED. The credential this request needs exists and could
 *              not be obtained, and the ambient cookie is not a substitute for
 *              it. The transport sends no cookie either, so the server answers
 *              401 rather than succeeding as somebody else.
 */
export type RequestCredential =
  { kind: 'bearer'; token: string } | { kind: 'ambient' } | { kind: 'none' };

/**
 * The staff credential, as the two questions this decision needs.
 *
 * `active` is «does the app currently claim an Entra staff identity» — the
 * same thing the panel renders — and NOT «did a token come back». That is the
 * whole distinction: a browser with no staff identity must still be able to
 * sign in to `/sa` with the dev OTP cookie, and a browser WITH one must never
 * silently fall through to it.
 *
 * ── AND `active` IS ASKED FIRST, WHICH IS THE OTHER HALF (#839) ───
 *
 * The first version asked `token()` first and returned the bearer whenever one
 * came back, consulting `active` only after a null. That is the SAME
 * substitution in the opposite direction, and it is reachable: `getApiToken`
 * reads MSAL's cache and never consults the state, `activeAccount` falls back
 * to `getAllAccounts()[0]`, and `signOut()` deliberately ends signed-out
 * LOCALLY even when `logoutPopup` throws — which leaves the account cached.
 * So an operator who signed Entra out, unsuccessfully, then used the dev OTP
 * door would have had their platform writes performed and audited as the Entra
 * account the app had already declared gone.
 *
 * A CACHED TOKEN IS NOT AUTHORITY TO SELECT AN ACTOR. The app's own state is,
 * and asking it first also means no MSAL round trip happens on a deployment
 * that has no Entra identity at all.
 */
export interface StaffCredential {
  /**
   * WHICH Entra identity the app claims, or null for none.
   *
   * An identity rather than a boolean, and compared ACROSS the await (#839).
   * `active(): boolean` could only answer «somebody», which cannot tell a
   * sign-out from a switch to a different staff account — and a bearer minted
   * for A and dispatched after the app moved to C is the same wrong-actor
   * substitution as the two already fixed, just spread over a few hundred
   * milliseconds of MSAL.
   *
   * The value is the Entra `oid` where there is one: the same immutable id the
   * API maps on, so «the same identity» means what the server means by it. It
   * falls back to the address and then to a bare marker — with neither, two
   * accounts are indistinguishable here and only the sign-out is caught, which
   * is a limit of what MSAL gave us rather than a choice.
   */
  identity(): string | null;
  token(): Promise<string | null>;
}

/**
 * THE one decision about what a request may authenticate with.
 *
 * It had a `bearerFor(context, staffToken)` sibling that answered
 * `string | null`. That is deleted rather than kept in step: with the ordering
 * above, a helper that cannot express «is an Entra identity active» has to
 * assume an answer, and either assumption is wrong somewhere — which is two
 * functions that can disagree about an auth boundary. It had no production
 * caller left once the transport moved here.
 */
export async function requestCredential(
  context: CustomerContext,
  staff: StaffCredential,
): Promise<RequestCredential> {
  if (context.surface === 'staff') {
    // NO ENTRA IDENTITY: the cookie is the credential here — the dev staff OTP
    // session, which a reload holds nothing else of. The token is not even
    // asked for, so a stale MSAL cache cannot answer a question nobody put.
    const before = staff.identity();
    if (!before) return { kind: 'ambient' };

    // A THROW IS THE SAME ANSWER AS NULL, decided here rather than left to the
    // transport's catch. `getApiToken` catches its own failures today and
    // returns null, so this is defence against a provider that stops doing
    // that.
    let token: string | null = null;
    try {
      token = await staff.token();
    } catch {
      token = null;
    }

    // ── THE IDENTITY IS RE-READ AFTER THE AWAIT (#839) ──────────────
    //
    // MSAL can take seconds, and anything can happen inside that window: the
    // operator signs out, or signs in as a different staff account. Reading
    // the state only BEFORE the await makes this function decide on a premise
    // that has since stopped being true — and the token it was already
    // fetching then goes out as an actor the app no longer claims to be. It is
    // the same substitution as the two already fixed, spread over the
    // acquisition instead of over a cache.
    //
    // A CHANGED IDENTITY FAILS CLOSED rather than falling through to
    // `ambient`. The cookie would be a THIRD actor — the dev OTP session, or a
    // customer's — so «the request failed» is the only answer here that is not
    // somebody else's name in an audit row.
    if (staff.identity() !== before) return { kind: 'none' };

    if (token) return { kind: 'bearer', token };

    // An Entra identity is claimed and its token could not be obtained: the
    // request must not succeed as whoever the cookie names. Transient or not,
    // «acted as the wrong person» is worse than «the request failed».
    return { kind: 'none' };
  }
  const customer = credentialFor(context);
  return customer ? { kind: 'bearer', token: customer } : { kind: 'ambient' };
}
